import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { lastValueFrom } from 'rxjs';
import { SharedModule } from '../../../../common/shared.module';
import { SwalService } from '../../../../common/services/swal.service';
import { ParametersService, SystemParameter } from '../../data-access/parameters.service';

/** Friendly names for known parameter keys. */
const PARAMETER_LABELS: Record<string, string> = {
    PassportExpirationYears: 'Vigencia máxima del pasaporte (años)',
};

@Component({
    selector: 'app-parameters-list',
    standalone: true,
    imports: [CommonModule, FormsModule, SharedModule],
    templateUrl: './parameters-list.component.html',
    providers: [SwalService],
})
export class ParametersListComponent implements OnInit {
    parameters: SystemParameter[] = [];
    /** Edited values by key. */
    drafts: Record<string, string> = {};
    loading = false;
    savingKey: string | null = null;

    constructor(
        private readonly parametersService: ParametersService,
        private readonly swal: SwalService,
    ) { }

    ngOnInit(): void {
        this.load();
    }

    label(p: SystemParameter): string {
        return PARAMETER_LABELS[p.key] ?? p.key;
    }

    isDirty(p: SystemParameter): boolean {
        return (this.drafts[p.key] ?? '').trim() !== p.value;
    }

    async load(): Promise<void> {
        this.loading = true;
        try {
            this.parameters = await lastValueFrom(this.parametersService.getAll());
            this.drafts = Object.fromEntries(this.parameters.map((p) => [p.key, p.value]));
        } catch {
            this.swal.error('No se pudieron cargar los parámetros.');
        } finally {
            this.loading = false;
        }
    }

    async save(p: SystemParameter): Promise<void> {
        const value = (this.drafts[p.key] ?? '').trim();
        if (!value) {
            this.swal.error('El valor es obligatorio.');
            return;
        }
        this.savingKey = p.key;
        try {
            const updated = await lastValueFrom(this.parametersService.update(p.key, value));
            this.parameters = this.parameters.map((x) => (x.key === p.key ? updated : x));
            this.drafts[p.key] = updated.value;
            this.swal.success('Parámetro actualizado.');
        } catch (e: any) {
            this.swal.error(e?.error?.error || 'No se pudo actualizar el parámetro.');
        } finally {
            this.savingKey = null;
        }
    }
}
